import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, Trash2, ArrowLeft } from 'lucide-react';
import { useCreateCommissionPlan, useUpdateCommissionPlan } from '@/hooks/finance/useCommissionPlans';
import type { CommissionPlanWithDetails } from '@/hooks/finance/useCommissionPlans';

interface Props {
  plan?: CommissionPlanWithDetails | null;
  onClose: () => void;
}

interface TierRow {
  min_goal_pct: number;
  max_goal_pct: number | null;
  commission_pct: number;
  sort_order: number;
}

interface AcceleratorRow {
  name: string;
  key: string;
  bonus_pct: number;
  description: string;
}

export default function CommissionPlanForm({ plan, onClose }: Props) {
  const createPlan = useCreateCommissionPlan();
  const updatePlan = useUpdateCommissionPlan();

  const [name, setName] = useState(plan?.name ?? '');
  const [effectiveFrom, setEffectiveFrom] = useState(plan?.effective_from ?? '');
  const [effectiveTo, setEffectiveTo] = useState(plan?.effective_to ?? '');
  const [minEntryPct, setMinEntryPct] = useState(plan?.min_entry_pct ?? 0);
  const [rampupType, setRampupType] = useState(plan?.rampup_type ?? '');
  const [rampupDays, setRampupDays] = useState(plan?.rampup_duration_days ?? 90);
  const [maxAccPct, setMaxAccPct] = useState(plan?.max_accelerator_pct ?? 3);
  const [status, setStatus] = useState(plan?.status ?? 'active');

  const [tiers, setTiers] = useState<TierRow[]>(
    plan?.commission_tiers?.map(t => ({
      min_goal_pct: t.min_goal_pct,
      max_goal_pct: t.max_goal_pct,
      commission_pct: t.commission_pct,
      sort_order: t.sort_order,
    })) ?? [
      { min_goal_pct: 60, max_goal_pct: 69.99, commission_pct: 6, sort_order: 1 },
      { min_goal_pct: 70, max_goal_pct: 79.99, commission_pct: 7, sort_order: 2 },
      { min_goal_pct: 80, max_goal_pct: 89.99, commission_pct: 8, sort_order: 3 },
      { min_goal_pct: 90, max_goal_pct: 99.99, commission_pct: 9, sort_order: 4 },
      { min_goal_pct: 100, max_goal_pct: 109.99, commission_pct: 10, sort_order: 5 },
      { min_goal_pct: 110, max_goal_pct: 119.99, commission_pct: 12, sort_order: 6 },
      { min_goal_pct: 120, max_goal_pct: null, commission_pct: 14, sort_order: 7 },
    ]
  );

  const [accelerators, setAccelerators] = useState<AcceleratorRow[]>(
    plan?.commission_accelerators?.map(a => ({
      name: a.name,
      key: a.key,
      bonus_pct: a.bonus_pct,
      description: a.description ?? '',
    })) ?? [
      { name: 'Meta Completa', key: 'meta_completa', bonus_pct: 1, description: 'Atingiu 100% da meta' },
      { name: 'Entrada Alta', key: 'entrada_alta', bonus_pct: 1, description: 'Down payment acima do threshold' },
      { name: 'Top Conversão', key: 'top_conversao', bonus_pct: 1, description: 'Taxa de conversão acima do threshold' },
    ]
  );

  const isEditing = !!plan;
  const isPending = createPlan.isPending || updatePlan.isPending;

  const handleSubmit = () => {
    const payload = {
      plan: {
        name,
        business_unit: 'us',
        effective_from: effectiveFrom,
        effective_to: effectiveTo || null,
        commission_base: 'cash_in',
        min_entry_pct: minEntryPct,
        rampup_type: rampupType || null,
        rampup_duration_days: rampupDays,
        rampup_config: {},
        max_accelerator_pct: maxAccPct,
        status,
      },
      tiers,
      accelerators: accelerators.map(a => ({ ...a, threshold_config: {} })),
    };

    if (isEditing && plan) {
      updatePlan.mutate(
        { planId: plan.id, ...payload },
        { onSuccess: onClose }
      );
    } else {
      createPlan.mutate(payload as any, { onSuccess: onClose });
    }
  };

  const addTier = () => {
    const last = tiers[tiers.length - 1];
    setTiers([...tiers, {
      min_goal_pct: (last?.max_goal_pct ?? last?.min_goal_pct ?? 0) + 0.01,
      max_goal_pct: null,
      commission_pct: (last?.commission_pct ?? 0) + 2,
      sort_order: tiers.length + 1,
    }]);
  };

  const removeTier = (idx: number) => setTiers(tiers.filter((_, i) => i !== idx));

  const updateTier = (idx: number, field: keyof TierRow, value: number | null) => {
    const updated = [...tiers];
    (updated[idx] as any)[field] = value;
    setTiers(updated);
  };

  const addAccelerator = () => {
    setAccelerators([...accelerators, { name: '', key: '', bonus_pct: 1, description: '' }]);
  };

  const removeAccelerator = (idx: number) => setAccelerators(accelerators.filter((_, i) => i !== idx));

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={onClose}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="text-2xl font-bold">{isEditing ? 'Editar Plano' : 'Novo Plano de Comissão'}</h1>
      </div>

      <Card>
        <CardHeader><CardTitle>Dados do Plano</CardTitle></CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          <div>
            <Label>Nome do Plano</Label>
            <Input value={name} onChange={e => setName(e.target.value)} placeholder="Ex: Consultor EUA - Mar 2025+" />
          </div>
          <div>
            <Label>Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Ativo</SelectItem>
                <SelectItem value="inactive">Inativo</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Vigência Início (YYYY-MM)</Label>
            <Input value={effectiveFrom} onChange={e => setEffectiveFrom(e.target.value)} placeholder="2025-03" />
          </div>
          <div>
            <Label>Vigência Fim (YYYY-MM, vazio = sem fim)</Label>
            <Input value={effectiveTo} onChange={e => setEffectiveTo(e.target.value)} placeholder="2025-12" />
          </div>
          <div>
            <Label>Entrada Mínima (%)</Label>
            <Input type="number" value={minEntryPct} onChange={e => setMinEntryPct(Number(e.target.value))} />
          </div>
          <div>
            <Label>Tipo de Ramp-up</Label>
            <Select value={rampupType} onValueChange={setRampupType}>
              <SelectTrigger><SelectValue placeholder="Nenhum" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Nenhum</SelectItem>
                <SelectItem value="bonus_pct">Bônus % por Mês</SelectItem>
                <SelectItem value="tier_doubled">Tier Dobrado</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Duração Ramp-up (dias)</Label>
            <Input type="number" value={rampupDays} onChange={e => setRampupDays(Number(e.target.value))} />
          </div>
          <div>
            <Label>Máx. Aceleradores (%)</Label>
            <Input type="number" value={maxAccPct} onChange={e => setMaxAccPct(Number(e.target.value))} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Escada de Tiers</CardTitle>
          <Button size="sm" variant="outline" onClick={addTier}><Plus className="h-4 w-4 mr-1" />Tier</Button>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Min %</TableHead>
                <TableHead>Max %</TableHead>
                <TableHead>Comissão %</TableHead>
                <TableHead className="w-12" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {tiers.map((tier, idx) => (
                <TableRow key={idx}>
                  <TableCell>
                    <Input type="number" value={tier.min_goal_pct} onChange={e => updateTier(idx, 'min_goal_pct', Number(e.target.value))} className="w-24" />
                  </TableCell>
                  <TableCell>
                    <Input type="number" value={tier.max_goal_pct ?? ''} onChange={e => updateTier(idx, 'max_goal_pct', e.target.value ? Number(e.target.value) : null)} className="w-24" placeholder="∞" />
                  </TableCell>
                  <TableCell>
                    <Input type="number" value={tier.commission_pct} onChange={e => updateTier(idx, 'commission_pct', Number(e.target.value))} className="w-24" />
                  </TableCell>
                  <TableCell>
                    <Button variant="ghost" size="icon" onClick={() => removeTier(idx)}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Aceleradores</CardTitle>
          <Button size="sm" variant="outline" onClick={addAccelerator}><Plus className="h-4 w-4 mr-1" />Acelerador</Button>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>Chave</TableHead>
                <TableHead>Bônus %</TableHead>
                <TableHead>Descrição</TableHead>
                <TableHead className="w-12" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {accelerators.map((acc, idx) => (
                <TableRow key={idx}>
                  <TableCell><Input value={acc.name} onChange={e => { const u = [...accelerators]; u[idx].name = e.target.value; setAccelerators(u); }} /></TableCell>
                  <TableCell><Input value={acc.key} onChange={e => { const u = [...accelerators]; u[idx].key = e.target.value; setAccelerators(u); }} className="w-36" /></TableCell>
                  <TableCell><Input type="number" value={acc.bonus_pct} onChange={e => { const u = [...accelerators]; u[idx].bonus_pct = Number(e.target.value); setAccelerators(u); }} className="w-20" /></TableCell>
                  <TableCell><Input value={acc.description} onChange={e => { const u = [...accelerators]; u[idx].description = e.target.value; setAccelerators(u); }} /></TableCell>
                  <TableCell>
                    <Button variant="ghost" size="icon" onClick={() => removeAccelerator(idx)}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onClose}>Cancelar</Button>
        <Button onClick={handleSubmit} disabled={isPending || !name || !effectiveFrom}>
          {isPending ? 'Salvando...' : isEditing ? 'Salvar Alterações' : 'Criar Plano'}
        </Button>
      </div>
    </div>
  );
}
